import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  DragIndicator,
  ExpandLess,
  ExpandMore,
  FolderOpen as FolderIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';

const STEPS = ['Upload Document', 'Review Sections', 'Done'];

const TEMPLATE_OPTIONS = [
  { value: '', label: 'Auto' },
  { value: 'horizon_europe', label: 'Horizon Europe', color: '#003399' },
  { value: 'erc', label: 'ERC', color: '#b5860c' },
  // { value: 'tubitak_1001', label: 'TÜBİTAK', color: '#E30A17' },
  { value: 'generic', label: 'Generic', color: '#2e7d32' },
];

const HORIZON_ROOTS = [
  { num: '1', label: 'Excellence' },
  { num: '2', label: 'Impact' },
  { num: '3', label: 'Implementation' },
];

// ── Numeric prefix helpers ────────────────────────────────────────────────────

const NUM_PREFIX_RE = /^(\d+(?:\.\d+)*)\.?\s+([\s\S]*)/;

function splitTitle(title) {
  const m = (title || '').match(NUM_PREFIX_RE);
  if (m) return { num: m[1], text: m[2] };
  return { num: null, text: title };
}

// ── Tree helpers ──────────────────────────────────────────────────────────────

function cloneTree(sections) {
  return sections.map(s => ({ ...s, children: cloneTree(s.children || []) }));
}

function findNode(sections, id, parent = null) {
  for (let i = 0; i < sections.length; i++) {
    if (sections[i]._id === id) return { node: sections[i], parent, siblings: sections, index: i };
    const r = findNode(sections[i].children || [], id, sections[i]);
    if (r) return r;
  }
  return null;
}

function applyOutdent(sections, id) {
  const tree = cloneTree(sections);
  const found = findNode(tree, id);
  if (!found || !found.parent) return tree;
  const parentId = found.parent._id;
  found.siblings.splice(found.index, 1);
  const pCtx = findNode(tree, parentId);
  if (pCtx) pCtx.siblings.splice(pCtx.index + 1, 0, found.node);
  return tree;
}

function applyIndent(sections, id) {
  const tree = cloneTree(sections);
  const found = findNode(tree, id);
  if (!found || found.index === 0) return tree;
  const prev = found.siblings[found.index - 1];
  found.siblings.splice(found.index, 1);
  prev.children = prev.children || [];
  prev.children.push(found.node);
  return tree;
}

function applyMoveUp(sections, id) {
  const tree = cloneTree(sections);
  const found = findNode(tree, id);
  if (!found || found.index === 0) return tree;
  const s = found.siblings;
  [s[found.index - 1], s[found.index]] = [s[found.index], s[found.index - 1]];
  return tree;
}

function applyMoveDown(sections, id) {
  const tree = cloneTree(sections);
  const found = findNode(tree, id);
  if (!found || found.index >= found.siblings.length - 1) return tree;
  const s = found.siblings;
  [s[found.index], s[found.index + 1]] = [s[found.index + 1], s[found.index]];
  return tree;
}

function applyDrop(sections, dragId, targetId, position) {
  if (dragId === targetId) return sections;
  const tree = cloneTree(sections);
  const dragCtx = findNode(tree, dragId);
  if (!dragCtx) return tree;
  // Prevent dropping onto own descendant
  if (findNode([dragCtx.node], targetId)) return tree;
  const { node: dragNode, siblings: dragSiblings, index: dragIndex } = dragCtx;
  dragSiblings.splice(dragIndex, 1);
  const targetCtx = findNode(tree, targetId);
  if (!targetCtx) return tree;
  const { node: targetNode, siblings: targetSiblings, index: targetIndex } = targetCtx;
  if (position === 'before') {
    targetSiblings.splice(targetIndex, 0, dragNode);
  } else if (position === 'after') {
    targetSiblings.splice(targetIndex + 1, 0, dragNode);
  } else {
    targetNode.children = [...(targetNode.children || []), dragNode];
  }
  return tree;
}

// ── ID assignment ─────────────────────────────────────────────────────────────

let _idCounter = 0;

function assignIds(sections) {
  return sections.map(s => ({
    ...s,
    _id: ++_idCounter,
    children: s.children ? assignIds(s.children) : [],
  }));
}

function collectAllIds(sections) {
  const ids = [];
  const walk = list => list.forEach(s => { ids.push(s._id); walk(s.children || []); });
  walk(sections);
  return ids;
}

function filterSelected(sections, selected) {
  const out = [];
  for (const s of sections) {
    if (!selected[s._id]) continue;
    out.push({ ...s, children: filterSelected(s.children || [], selected) });
  }
  return out;
}

function cleanForApi(sections) {
  return sections.map(({ _id, content_preview, children, ...rest }) => ({
    ...rest,
    children: cleanForApi(children || []),
  }));
}

// ── Sequential numbering ──────────────────────────────────────────────────────

// Compute sequential position numbers (e.g. "1", "1.2", "2.3.1") for every
// SELECTED section based on its position among checked siblings.
// Unchecked sections are skipped and don't consume a slot in the sequence.
function computeNumbers(sections, selected, prefix = '') {
  const result = {};
  let counter = 0;
  for (const s of sections) {
    if (!selected[s._id]) continue;
    counter++;
    const num = prefix ? `${prefix}.${counter}` : String(counter);
    result[s._id] = num;
    Object.assign(result, computeNumbers(s.children || [], selected, num));
  }
  return result;
}

// Strip numeric prefix from every section title before import.
// The sequential position is already encoded in section_key; titles should
// read as plain headings without leading "1.2" noise.
function applyComputedNumbers(sections, numbers) {
  return sections.map(s => {
    const { text } = splitTitle(s.title);
    return {
      ...s,
      title: text,
      children: applyComputedNumbers(s.children || [], numbers),
    };
  });
}

// ── Horizon Europe auto-structure ─────────────────────────────────────────────

function applyHorizonStructure(sections) {
  function rootNumOf(s) {
    const { num } = splitTitle(s.title);
    if (!num) return null;
    return num.includes('.') ? null : num;
  }
  const existingRootNums = new Set(sections.map(rootNumOf).filter(Boolean));
  const missingRoots = HORIZON_ROOTS.filter(r => !existingRootNums.has(r.num));
  if (missingRoots.length === 0) return sections;
  const missingNums = new Set(missingRoots.map(r => r.num));
  const toGroup = new Map();
  const keep = [];
  for (const s of sections) {
    const { num } = splitTitle(s.title);
    const rootNum = num ? num.split('.')[0] : null;
    if (rootNum && missingNums.has(rootNum)) {
      if (!toGroup.has(rootNum)) toGroup.set(rootNum, []);
      toGroup.get(rootNum).push(s);
    } else {
      keep.push(s);
    }
  }
  const newRoots = missingRoots.map(({ num, label }) => ({
    _id: ++_idCounter,
    title: `${num} ${label}`,
    content: '',
    content_preview: '',
    children: toGroup.get(num) || [],
  }));
  const all = [...keep, ...newRoots];
  all.sort((a, b) => {
    const na = splitTitle(a.title).num;
    const nb = splitTitle(b.title).num;
    const ia = na ? parseInt(na.split('.')[0], 10) : -1;
    const ib = nb ? parseInt(nb.split('.')[0], 10) : -1;
    if (ia < 0 && ib < 0) return 0;
    if (ia < 0) return -1;
    if (ib < 0) return 1;
    return ia - ib;
  });
  return all;
}

// ── Section tree node ─────────────────────────────────────────────────────────

function SectionNode({
  section, depth, selected, onToggle,
  isFirst, isLast, hasParent,
  onIndent, onOutdent, onMoveUp, onMoveDown,
  dropIndicator, onDragStart, onDragOver, onDrop, onDragEnd,
  computedNumbers,
}) {
  const [open, setOpen] = useState(depth < 2);
  const [hovered, setHovered] = useState(false);
  const rowRef = useRef();
  const hasChildren = (section.children || []).length > 0;
  const { text } = splitTitle(section.title);
  // Use the live sequential number (updates when siblings are excluded/reordered)
  const displayNum = computedNumbers?.[section._id] ?? null;
  const isChecked  = !!selected[section._id];
  const dropPos    = dropIndicator?.id === section._id ? dropIndicator.position : null;

  const computePosition = (e) => {
    if (!rowRef.current) return 'after';
    const { top, height } = rowRef.current.getBoundingClientRect();
    const y = e.clientY - top;
    if (y < height * 0.3) return 'before';
    if (y > height * 0.7) return 'after';
    return 'child';
  };

  const handleDragStart = (e) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    onDragStart(section._id);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    onDragOver(section._id, computePosition(e));
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDrop(section._id);
  };

  return (
    <Box sx={{ ml: depth * 2.5 }}>
      <Box
        ref={rowRef}
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnd={onDragEnd}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          borderRadius: 0.5,
          ...(dropPos === 'before' && {
            borderTop: '2px solid',
            borderTopColor: 'primary.main',
          }),
          ...(dropPos === 'after' && {
            borderBottom: '2px solid',
            borderBottomColor: 'primary.main',
          }),
          ...(dropPos === 'child' && {
            background: 'rgba(25,118,210,0.08)',
            outline: '1px solid rgba(25,118,210,0.3)',
          }),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ py: 0.25, pr: 0.5 }}>
          {/* expand / collapse */}
          {hasChildren ? (
            <IconButton size="small" onClick={() => setOpen(o => !o)} sx={{ p: 0.25 }}>
              {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
            </IconButton>
          ) : (
            <Box sx={{ width: 24 }} />
          )}

          <Checkbox
            size="small"
            checked={!!selected[section._id]}
            onChange={() => onToggle(section._id)}
            sx={{ p: 0.25 }}
          />

          {/* controls: drag handle + arrows — visible on hover, before title */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0}
            sx={{ visibility: hovered ? 'visible' : 'hidden', flexShrink: 0 }}
          >
            <DragIndicator
              sx={{ fontSize: 16, color: 'text.disabled', cursor: 'grab', mr: 0.25 }}
            />
            <Tooltip title="Move up">
              <span>
                <IconButton size="small" disabled={isFirst} onClick={() => onMoveUp(section._id)} sx={{ p: 0.25 }}>
                  <ArrowUpward sx={{ fontSize: 13 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down">
              <span>
                <IconButton size="small" disabled={isLast} onClick={() => onMoveDown(section._id)} sx={{ p: 0.25 }}>
                  <ArrowDownward sx={{ fontSize: 13 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Outdent — move out of parent">
              <span>
                <IconButton size="small" disabled={!hasParent} onClick={() => onOutdent(section._id)} sx={{ p: 0.25 }}>
                  <KeyboardArrowLeft sx={{ fontSize: 13 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Indent — nest under section above">
              <span>
                <IconButton size="small" disabled={isFirst} onClick={() => onIndent(section._id)} sx={{ p: 0.25 }}>
                  <KeyboardArrowRight sx={{ fontSize: 13 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* number badge + title */}
          <Box sx={{ flex: 1, minWidth: 0, opacity: isChecked ? 1 : 0.38 }}>
            <Stack direction="row" alignItems="baseline" spacing={0.75}>
              {displayNum && (
                <Typography
                  component="span"
                  variant="caption"
                  fontFamily="monospace"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ flexShrink: 0, lineHeight: 1.3 }}
                >
                  {displayNum}
                </Typography>
              )}
              <Typography
                variant={depth === 0 ? 'subtitle2' : 'body2'}
                fontWeight={depth === 0 ? 600 : 400}
                sx={{ lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {text}
              </Typography>
            </Stack>
            {section.content_preview && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 440 }}
              >
                {section.content_preview}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {hasChildren && (
        <Collapse in={open}>
          {section.children.map((child, idx) => (
            <SectionNode
              key={child._id}
              section={child}
              depth={depth + 1}
              selected={selected}
              onToggle={onToggle}
              isFirst={idx === 0}
              isLast={idx === section.children.length - 1}
              hasParent
              onIndent={onIndent}
              onOutdent={onOutdent}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              dropIndicator={dropIndicator}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
              computedNumbers={computedNumbers}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────

export default function ImportProposalDialog({
  open,
  onClose,
  proposalId,
  existingSectionCount = 0,
  onSuccess,
}) {
  const fileRef = useRef();
  const [step, setStep]               = useState(0);
  const [file, setFile]               = useState(null);
  const [template, setTemplate]       = useState('');
  const [sections, setSections]       = useState([]);
  const [selected, setSelected]       = useState({});
  const [withContent, setWithContent] = useState(true);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [importedCount, setImportedCount] = useState(0);
  const [confirming, setConfirming]   = useState(false);

  // Drag-and-drop state — ref for the dragged ID (avoids stale closures in event handlers)
  const dragIdRef          = useRef(null);
  const dropIndicatorRef   = useRef(null);
  const [dropIndicator, setDropIndicator] = useState(null);

  const reset = () => {
    setStep(0); setFile(null); setTemplate(''); setSections([]); setSelected({});
    setLoading(false); setError(null); setImportedCount(0); setConfirming(false);
    dragIdRef.current = null; dropIndicatorRef.current = null; setDropIndicator(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const onDrop = useCallback(e => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.name.match(/\.(docx|pdf)$/i)) setFile(f);
  }, []);

  const handleExtract = async () => {
    if (!file) { setError('Please select a .docx or .pdf file.'); return; }
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axiosService.post(
        `/proposal/proposals/${proposalId}/extract_proposal_document/`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      let withIds = assignIds(res.data.sections || []);
      if (template === 'horizon_europe') withIds = applyHorizonStructure(withIds);
      setSections(withIds);
      setSelected(Object.fromEntries(collectAllIds(withIds).map(id => [id, true])));
      setStep(1);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to parse document.');
    } finally {
      setLoading(false);
    }
  };

  // Recompute sequential numbers whenever selection or tree order changes
  const computedNumbers = useMemo(
    () => computeNumbers(sections, selected),
    [sections, selected],
  );

  const handleImport = async () => {
    if (existingSectionCount > 0 && !confirming) { setConfirming(true); return; }
    // Apply current sequential numbers to titles before sending so stored titles
    // reflect final numbering (e.g. "1.2" not the stale "1.3" from the document)
    const renumbered = applyComputedNumbers(sections, computedNumbers);
    const toImport   = filterSelected(renumbered, selected);
    if (toImport.length === 0) { setError('Select at least one section.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await axiosService.post(
        `/proposal/proposals/${proposalId}/import_proposal_sections/`,
        { sections: cleanForApi(toImport), force: existingSectionCount > 0, with_content: withContent },
      );
      setImportedCount(res.data.length);
      setStep(2);
      onSuccess?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Import failed.');
      setConfirming(false);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = id => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const modifySections = fn => setSections(prev => fn(prev));
  const handleIndent   = id => modifySections(s => applyIndent(s, id));
  const handleOutdent  = id => modifySections(s => applyOutdent(s, id));
  const handleMoveUp   = id => modifySections(s => applyMoveUp(s, id));
  const handleMoveDown = id => modifySections(s => applyMoveDown(s, id));

  // Drag handlers — stable refs prevent unnecessary re-renders of entire tree
  const handleDragStart = useCallback((id) => {
    dragIdRef.current = id;
  }, []);

  const handleDragOver = useCallback((id, position) => {
    if (id === dragIdRef.current) return;
    const curr = dropIndicatorRef.current;
    if (curr?.id === id && curr?.position === position) return;
    const next = { id, position };
    dropIndicatorRef.current = next;
    setDropIndicator(next);
  }, []);

  const handleDrop = useCallback((targetId) => {
    const dragId    = dragIdRef.current;
    const indicator = dropIndicatorRef.current;
    if (dragId && indicator && dragId !== targetId) {
      setSections(prev => applyDrop(prev, dragId, targetId, indicator.position));
      // Keep selection consistent — new node keeps its id
      setSelected(prev => {
        if (prev[dragId] === undefined) return prev;
        return { ...prev };
      });
    }
    dragIdRef.current = null;
    dropIndicatorRef.current = null;
    setDropIndicator(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    dragIdRef.current = null;
    dropIndicatorRef.current = null;
    setDropIndicator(null);
  }, []);

  const selectCount = Object.values(selected).filter(Boolean).length;
  const totalCount  = collectAllIds(sections).length;
  const selectAll   = () => setSelected(Object.fromEntries(collectAllIds(sections).map(id => [id, true])));
  const selectNone  = () => setSelected({});

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FolderIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>Import Proposal Document</Typography>
        </Stack>
        <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* ── Step 0: Upload ────────────────────────────────────────────── */}
        {step === 0 && (
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              Upload a previously written proposal (.docx) or a submitted proposal PDF (.pdf).
              Section headings will be extracted automatically and imported as editable sections.
            </Typography>

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                DOCUMENT TYPE (optional — enables structure auto-detection)
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {TEMPLATE_OPTIONS.map(opt => {
                  const active = template === opt.value;
                  return (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      onClick={() => setTemplate(opt.value)}
                      variant={active ? 'filled' : 'outlined'}
                      size="small"
                      sx={active && opt.color
                        ? { bgcolor: opt.color, color: '#fff', fontWeight: 700, '&:hover': { bgcolor: opt.color } }
                        : active ? { fontWeight: 700 } : {}}
                    />
                  );
                })}
              </Stack>
              {template === 'horizon_europe' && (
                <Typography variant="caption" color="primary.main" sx={{ mt: 0.75, display: 'block' }}>
                  If the main sections (Excellence / Impact / Implementation) are missing,
                  they will be added automatically and sub-sections nested under them.
                </Typography>
              )}
            </Box>

            <Box
              onDrop={onDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileRef.current?.click()}
              sx={{
                border: '2px dashed',
                borderColor: file ? 'success.main' : 'divider',
                borderRadius: 1,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: file ? 'success.50' : 'background.paper',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              <UploadIcon sx={{ fontSize: 48, color: file ? 'success.main' : 'text.secondary', mb: 1 }} />
              {file ? (
                <>
                  <Typography fontWeight={600} color="success.dark">{file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024 / 1024).toFixed(1)} MB — click to change
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Drag & drop a .docx or .pdf file here, or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.disabled">Microsoft Word (.docx) or PDF (.pdf)</Typography>
                </>
              )}
              <input ref={fileRef} type="file" accept=".docx,.pdf" style={{ display: 'none' }}
                onChange={e => setFile(e.target.files[0] || null)} />
            </Box>
          </Stack>
        )}

        {/* ── Step 1: Review ───────────────────────────────────────────── */}
        {step === 1 && (
          <Stack spacing={1.5}>
            {confirming && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1 }}>
                This proposal already has <strong>{existingSectionCount}</strong> section
                {existingSectionCount !== 1 ? 's' : ''}. Importing will replace all
                existing sections. Click <strong>Import</strong> again to confirm.
              </Alert>
            )}

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {selectCount} of {totalCount} sections selected.
                Drag rows or use the arrows to reorder and re-nest.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={selectAll}>Select all</Button>
                <Button size="small" onClick={selectNone}>None</Button>
              </Stack>
            </Stack>

            <FormControlLabel
              control={
                <Checkbox size="small" checked={withContent}
                  onChange={e => setWithContent(e.target.checked)} />
              }
              label={<Typography variant="body2">Import section text content (uncheck for structure only)</Typography>}
            />

            <Divider />

            <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
              {sections.length === 0
                ? <Alert severity="warning">No sections found in the document.</Alert>
                : sections.map((s, idx) => (
                  <SectionNode
                    key={s._id}
                    section={s}
                    depth={0}
                    selected={selected}
                    onToggle={toggleSection}
                    isFirst={idx === 0}
                    isLast={idx === sections.length - 1}
                    hasParent={false}
                    onIndent={handleIndent}
                    onOutdent={handleOutdent}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    dropIndicator={dropIndicator}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    computedNumbers={computedNumbers}
                  />
                ))
              }
            </Box>
          </Stack>
        )}

        {/* ── Step 2: Done ─────────────────────────────────────────────── */}
        {step === 2 && (
          <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
            <Typography variant="h5" fontWeight={700}>Import Complete</Typography>
            <Typography variant="body1" color="text.secondary">
              <strong>{importedCount}</strong> top-level section
              {importedCount !== 1 ? 's' : ''} created successfully.
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 0 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleExtract}
              disabled={!file || loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            >
              {loading ? 'Extracting…' : 'Extract Sections'}
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button onClick={() => { setStep(0); setConfirming(false); setError(null); }} disabled={loading}>
              Back
            </Button>
            <Button
              variant="contained"
              color={confirming ? 'warning' : 'primary'}
              onClick={handleImport}
              disabled={loading || selectCount === 0}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {loading
                ? 'Importing…'
                : confirming
                ? `Replace & Import ${selectCount} section${selectCount !== 1 ? 's' : ''}`
                : `Import ${selectCount} section${selectCount !== 1 ? 's' : ''}`}
            </Button>
          </>
        )}
        {step === 2 && <Button variant="contained" onClick={handleClose}>Done</Button>}
      </DialogActions>
    </Dialog>
  );
}
